/**
 * Utility function to transform local image paths to Cloudinary CDN URLs
 * This can be used in templates and components
 * 
 * Usage in template:
 * ```vue
 * <img :src="$cdnImage('/images/photo.jpg')" alt="Photo" />
 * ```
 * 
 * Or in script:
 * ```vue
 * <script setup>
 * const { $cdnImage } = useNuxtApp()
 * </script>
 * ```
 */
import { useRuntimeConfig } from '#imports'

// Cache for mapping
let mappingCache: Record<string, string> | null = null

function getMapping(): Record<string, string> {
  if (mappingCache !== null) {
    return mappingCache
  }

  if (import.meta.server) {
    // Server-side: read from file
    try {
      const fs = require('node:fs')
      const path = require('node:path')
      const mappingPath = path.resolve(process.cwd(), 'data', 'public-images.json')
      if (fs.existsSync(mappingPath)) {
        const raw = fs.readFileSync(mappingPath, 'utf-8')
        mappingCache = JSON.parse(raw || '{}')
        return mappingCache
      }
    } catch (e) {
      // Ignore errors
    }
  } else {
    // Client-side: get from window global
    try {
      // @ts-ignore
      mappingCache = window.__publicImagesMapping || {}
      return mappingCache
    } catch (e) {
      // Ignore errors
    }
  }

  mappingCache = {}
  return mappingCache
}

export function cdnImage(src: string): string {
  if (!src || !src.startsWith('/')) {
    return src
  }

  const config = useRuntimeConfig()
  const cloudBase: string = (config.public as any)?.cloudinaryBaseURL || 'https://res.cloudinary.com/ommi-shop/image/upload'
  const mapping = getMapping()
  const cloudinaryPublicId = mapping[src]

  if (cloudinaryPublicId) {
    // Extract extension from original path
    const extMatch = src.match(/\.[^.]+$/)
    const ext = extMatch ? extMatch[0] : ''
    // Always append extension - Cloudinary may have stored with double extension (.jpg.jpg)
    // when we uploaded with extension in public_id, so we need to match that
    const publicIdWithExt = ext ? `${cloudinaryPublicId}${ext}` : cloudinaryPublicId
    // Build Cloudinary URL with WebP optimization
    return `${cloudBase}/f_auto,q_auto/${publicIdWithExt}`
  }

  return src
}

